import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

// Module-level dedup so React StrictMode's double-mount doesn't burn a token
// (first call would succeed, second would see "used" and overwrite the UI).
const inFlight = new Map(); // token -> Promise<{ok, message}>

function verifyOnce(token) {
  if (inFlight.has(token)) return inFlight.get(token);
  const p = api.post("/auth/verify-email", { token })
    .then((r) => ({ ok: true, message: r.data?.message || "Email verified." }))
    .catch((e) => ({ ok: false, message: formatApiError(e.response?.data?.detail) || "Verification failed." }));
  inFlight.set(token, p);
  return p;
}

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState(token ? "loading" : "missing"); // loading | success | error | missing
  const [message, setMessage] = useState("");
  const firedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    if (firedRef.current) return;
    firedRef.current = true;
    (async () => {
      const res = await verifyOnce(token);
      setMessage(res.message);
      setState(res.ok ? "success" : "error");
        if (res.ok && user) {
          try { await refreshUser(); } catch (e) { console.warn("[refreshUser] post-verify failed", e?.message); }
        }
    })();
  }, [token]);

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="verify-email-page">
      <div className="overline mb-3">Email verification</div>
      <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight mb-8">
        {state === "success" ? "You're verified" : "Verifying your email"}
      </h1>
      <Card className="p-8 rounded-2xl border-border shadow-none text-center">
        {state === "loading" && (
          <div className="flex flex-col items-center gap-4 py-6" data-testid="verify-loading">
            <Loader2 className="w-8 h-8 animate-spin text-primary" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">Checking your verification link…</p>
          </div>
        )}
        {state === "success" && (
          <div className="flex flex-col items-center gap-4 py-6" data-testid="verify-success">
            <CheckCircle2 className="w-10 h-10 text-primary" strokeWidth={1.5} />
            <p className="text-base text-foreground">{message}</p>
            <Link to={user ? "/dashboard" : "/login"}>
              <Button className="rounded-full cta-saffron mt-2" data-testid="verify-continue">
                {user ? "Go to dashboard" : "Log in"}
              </Button>
            </Link>
          </div>
        )}
        {state === "error" && (
          <div className="flex flex-col items-center gap-4 py-6" data-testid="verify-error">
            <XCircle className="w-10 h-10 text-destructive" strokeWidth={1.5} />
            <p className="text-base text-foreground">{message}</p>
            <p className="text-xs text-muted-foreground">
              The link may be expired or already used. Log in and click <em>Resend</em> from the banner.
            </p>
            <Link to={user ? "/dashboard" : "/login"}>
              <Button variant="ghost" className="rounded-full mt-2" data-testid="verify-back">
                {user ? "Back to dashboard" : "Back to login"}
              </Button>
            </Link>
          </div>
        )}
        {state === "missing" && (
          <div className="flex flex-col items-center gap-4 py-6" data-testid="verify-missing">
            <XCircle className="w-10 h-10 text-muted-foreground" strokeWidth={1.5} />
            <p className="text-base text-foreground">No verification token provided.</p>
            <p className="text-xs text-muted-foreground">Open the verification link from your email.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
